import { boundaryLoopsOfFaceSet } from './boundary.js';
import { edgeId, faceNormal } from './edges.js';
import { coplanarRegion } from './regions.js';

// -----------------------------------------------------------------------
// Geometric adjacency (derived, non-persisted)
// -----------------------------------------------------------------------
//
// Prerequisite layer for Bevel/Loop Cut, anticipated by this module's own
// header comment ("Welding coincident vertices into shared corners is
// deferred to whichever future operation ... first needs it"). This is that
// operation's shared foundation — it does not itself mutate anything.
//
// THE PROBLEM: canonical topology is render-vertex granularity (see the
// module doc above `captureTopology`) — every hard edge duplicates its
// endpoint vertices once per adjacent face, because Three.js primitives need
// a separate vertex per shading normal. Measured on `THREE.BoxGeometry`:
// 24 vertices at only 8 distinct positions, 30 raw `edgeId()`-derived edges,
// of which the 6 with two adjacent faces are the INVISIBLE diagonals inside
// each flat side, and the 24 with exactly one adjacent face are the 12
// VISIBLE hard edges, each split into two vertex-disjoint halves. So on an
// ordinary primitive, "two adjacent faces" and "one user-meaningful hard
// edge" are disjoint, complementary sets — a consumer that needs "the edge
// between these two visible faces" cannot get it from raw topology edges at
// all; it has to reconstruct it from position coincidence.
//
// THE FIX: recompute, on demand, three derived concepts layered on top of
// existing topology primitives, reusing them rather than re-deriving:
//   - regions:  coplanar face patches, via repeated `coplanarRegion` calls
//               (unchanged) — one call per not-yet-visited face.
//   - corners:  groups of topology vertex ids that occupy one real-world
//               point, established ONLY by successfully pairing the
//               boundary edges that meet there (see below) — never by a
//               blind global "same position" pass.
//   - edges:    the boundary between two regions, reassembled from either
//               (a) an already-shared raw vertex-id edge between two
//               different regions (a hand-authored/welded topology, e.g. an
//               `addMesh` call that shares ids across a hard edge — no
//               pairing needed, it is already one edge), or (b) two
//               position-coincident, vertex-disjoint 1-adjacent-face
//               boundary edges from two different regions (the render-vertex
//               -duplicated case every primitive actually produces).
//
// TOLERANCE: exact position-tuple equality, no epsilon. There is no existing
// position-coincidence tolerance anywhere in this module to reuse — every
// existing epsilon here is either an angular/normal-dot-product tolerance
// (`RENDER_NORMAL_EPSILON`, `REGION_NORMAL_EPSILON`, ...) or a scale-relative
// AREA tolerance (`TRIANGLE_AREA_EPSILON`); none of them are about "are these
// two points the same point." Exact equality is sufficient because
// `captureTopology` reads duplicated corners straight out of the same
// underlying Float32 buffer values (bit-identical by construction, verified
// across plain/rotated/non-uniformly-scaled/far-translated `BufferGeometry`),
// and every existing command that mints a vertex meant to be shared (most
// notably `computeConformingSubdivision`'s conforming midpoints) computes it
// ONCE and reuses that exact value everywhere it's needed rather than
// recomputing it per caller — verified empirically by chaining
// subdivide→inset→scale→extrude on a cube and confirming zero near-miss
// pairs: every pair of vertices is either at the identical position (exact
// float64 equality) or at least ~0.14 units apart, a >12-order-of-magnitude
// gap from float noise. Introducing an epsilon here would only add risk
// (wrongly merging distinct, close corners on a small-scale or dense mesh)
// for no observed benefit.
//
// WHY NOT GLOBAL POSITION GROUPING: naive "same position anywhere in the
// topology => same corner" is unsafe. Two disconnected pieces (e.g. two
// separate shells, or a stray triangle) can legitimately occupy identical
// coordinates without being part of the same surface, and welding them would
// invent adjacency — a future Bevel walking "every region touching this
// corner" would silently reach into unrelated geometry. The guard used here
// is a LOCAL, structural one already established as this codebase's manifold
// invariant (`boundaryLoopOfFaceSet`'s own doc: "Two triangles sharing an
// edge always traverse it in *opposite* directions if both are consistently
// CCW-wound"): two position-coincident boundary edges from different regions
// are only merged into one geometric edge when they ALSO run in opposite
// directions relative to each other. Verified on the cube: all 12 real hard
// edges pair with opposite winding (12/12); a deliberately same-winding
// coincident pair (see the disconnected-shells test) is correctly left
// unmerged. Corners then emerge FROM validated edges, not independently of
// them: a corner's member vertex ids are exactly the endpoints unioned
// together by successfully-paired edges (transitively — a cube corner's 3
// vertex ids end up in one group because each of the 3 hard edges meeting
// there independently unions two of them). A vertex that never participates
// in a validated pairing — including one that merely happens to share a
// coordinate with something unrelated — stays its own singleton corner. This
// is not a 100%-guaranteed distinguisher (two disconnected pieces could be
// deliberately constructed to also satisfy opposite winding), but that is an
// irreducible ambiguity of pure geometry: at that point the two pieces are,
// for every purpose position and winding can observe, a genuine seam.
//
// IDENTITY: corner/region/edge ids are derived from CONTENT (sorted member
// ids), never from discovery/traversal order, so two topology objects that
// describe the same mesh with their `vertices`/`faces` arrays in a different
// order produce byte-identical derived adjacency. A corner's id is
// `JSON.stringify` of its sorted member vertex ids (always injective for a
// set of strings, so it needs no `edgeId`-style dual encoding). A region's id
// is the same construction over its sorted member face ids. A geometric
// edge's id reuses `edgeId()` unchanged, called on its two corner ids instead
// of raw vertex ids — corner ids are never alphanumeric-only (they always
// start with `[`), so this always takes `edgeId`'s already-injective
// `JSON.stringify(pair)` branch. None of these ids are persisted or exposed
// as command arguments yet; they are stable only for one already-computed
// topology snapshot, not across topology-mutating operations.
//
// PERFORMANCE: `allCoplanarRegions` calls `coplanarRegion()` once per
// region, and each `coplanarRegion()` call rebuilds a whole-topology
// edge -> incident-faces adjacency map before it BFS-walks a single region
// — so region discovery here is approximately
// O(number_of_regions × topology_size), not linear in mesh size, worse on a
// topology with many small coplanar regions than on one with few large ones.
// The remaining work is linear/near-linear: boundary-edge position-pairing
// buckets candidates by an exact endpoint-position-pair string key in a
// `Map` — O(B) average, not the O(B²) all-pairs comparison a naive approach
// would need — and corner grouping is union-find with path compression,
// near O(V·alpha(V)). No caching is introduced; every call recomputes from
// the topology passed in, exactly like `edgesOfTopology`/`coplanarRegion`
// already do.

/** Deterministic string key for exact position-tuple equality — no rounding. */
function exactPositionKey(position) {
  return position.join(',');
}

/** Minimal union-find with path compression, keyed by arbitrary string ids. */
function createUnionFind() {
  const parent = new Map();
  function find(x) {
    if (!parent.has(x)) parent.set(x, x);
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root);
    let cursor = x;
    while (parent.get(cursor) !== root) {
      const next = parent.get(cursor);
      parent.set(cursor, root);
      cursor = next;
    }
    return root;
  }
  function union(a, b) {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  }
  return { find, union };
}

/** `JSON.stringify` of a sorted id list — always injective for a set of strings, needs no `edgeId`-style dual encoding. */
function contentId(ids) {
  return JSON.stringify([...ids].sort());
}

/**
 * `faceNormal`'s cross product can legitimately land on `-0` in one
 * component for one triangle of a coplanar region and `+0` for a sibling
 * triangle of the exact same region (same true direction, different
 * intermediate floating-point cancellation) — which triangle a region's
 * normal is computed from depends on `topology.faces` array order, an
 * incidental detail this layer's determinism guarantee should not leak.
 * `-0`/`0` are numerically identical everywhere except a sign-bit-sensitive
 * comparison (`Object.is`, `JSON.stringify`); normalized once here so a
 * region's reported normal never depends on which member face was scanned
 * first.
 */
function canonicalizeZero(vector) {
  return vector.map((component) => (Object.is(component, -0) ? 0 : component));
}

/**
 * Every coplanar region in the topology, via repeated `coplanarRegion` calls
 * seeded from each not-yet-visited face in `topology.faces` order — the same
 * discovery `extrudeRegion`/`insetRegion`/`subdivideRegion` already perform
 * once per call, just exhaustively here. Region discovery is read-only and
 * accepts every structurally valid coplanar component, including one with an
 * inner boundary left when a supported operation extrudes an inset cap.
 */
function allCoplanarRegions(topology) {
  const faceById = new Map(topology.faces.map((face) => [face.id, face]));
  const visited = new Set();
  const regions = [];
  for (const face of topology.faces) {
    if (visited.has(face.id)) continue;
    const memberFaceIds = coplanarRegion(topology, face.id);
    for (const id of memberFaceIds) visited.add(id);
    // Content-stable, not traversal-order-stable: the lexicographically
    // smallest member face id, not whichever face happened to seed this BFS
    // (which depends on `topology.faces` array order). Member faces can have
    // slightly different computed normals despite being within
    // REGION_NORMAL_EPSILON of each other, so which one is sampled is
    // observable — see the module's determinism guarantee.
    const stableFaceId = [...memberFaceIds].sort()[0];
    const normal = canonicalizeZero(faceNormal(topology, stableFaceId));
    const memberFaces = memberFaceIds.map((id) => faceById.get(id));
    const boundaryLoops = boundaryLoopsOfFaceSet(memberFaces, 'computeGeometricAdjacency');
    regions.push({
      faceIds: [...memberFaceIds].sort(),
      normal,
      boundaryVertexIds: [...new Set(boundaryLoops.flat())].sort(),
    });
  }
  return regions;
}

/**
 * Compute the derived geometric adjacency layer for `topology`: regions,
 * geometric corners, and geometric edges, cross-referenced by id. Read-only
 * — never mutates `topology` — and non-persisted; call again after any
 * topology-changing operation rather than trying to keep a prior result in
 * sync. See the module-level comment above for the full rationale.
 *
 * Every unmarked internal triangulation diagonal (a raw edge with two
 * adjacent faces in the SAME region — e.g. a box quad's hidden diagonal) is
 * reported separately in `diagonalTopologyEdgeIds` and never appears as a
 * geometric edge or contributes to corner grouping. A persisted explicit
 * edge is user/modeling-significant and flows through normal aggregation.
 */
export function computeGeometricAdjacency(topology) {
  const positionById = new Map(topology.vertices.map((vertex) => [vertex.id, vertex.position]));
  const explicitEdgeIds = new Set(topology.explicitEdgeIds ?? []);
  const regionsRaw = allCoplanarRegions(topology);
  const regionIndexByFaceId = new Map();
  regionsRaw.forEach((region, index) => {
    for (const faceId of region.faceIds) regionIndexByFaceId.set(faceId, index);
  });
  const regionIds = regionsRaw.map((region) => contentId(region.faceIds));

  // Classify every raw topology edge by adjacent-face count and region
  // membership: internal diagonal, already-complete (welded, two different
  // regions sharing a raw vertex-id edge), or a 1-face boundary candidate
  // that needs a position+winding partner from elsewhere.
  const rawAdjacency = new Map();
  topology.faces.forEach((face) => {
    const ids = face.vertexIds;
    for (let i = 0; i < 3; i++) {
      const a = ids[i];
      const b = ids[(i + 1) % 3];
      const key = edgeId(a, b);
      const entry = { faceId: face.id, a, b };
      const list = rawAdjacency.get(key);
      if (list) list.push(entry);
      else rawAdjacency.set(key, [entry]);
    }
  });

  const diagonalTopologyEdgeIds = [];
  const singleFaceCandidates = [];
  const weldedComplete = [];
  for (const [rawKey, occurrences] of rawAdjacency) {
    if (occurrences.length === 1) {
      const [o] = occurrences;
      singleFaceCandidates.push({ rawKey, ...o, regionIndex: regionIndexByFaceId.get(o.faceId) });
    } else if (occurrences.length === 2) {
      const [o1, o2] = occurrences;
      const region1 = regionIndexByFaceId.get(o1.faceId);
      const region2 = regionIndexByFaceId.get(o2.faceId);
      if (region1 === region2 && !explicitEdgeIds.has(rawKey)) {
        diagonalTopologyEdgeIds.push(rawKey);
      } else {
        weldedComplete.push({
          rawKey,
          occurrences: [
            { ...o1, rawKey, regionIndex: region1 },
            { ...o2, rawKey, regionIndex: region2 },
          ],
        });
      }
    }
    // >2 adjacent faces sharing one raw vertex-id edge is non-manifold at the
    // raw-topology level. No current command produces it, but a hand-authored
    // `addMesh` could; it is excluded from both diagonals and boundary
    // candidates rather than guessed at.
  }

  const unionFind = createUnionFind();
  for (const vertex of topology.vertices) unionFind.find(vertex.id);

  // Bucket 1-face boundary candidates by their unordered endpoint-position-
  // pair key (O(B) average, not O(B^2) all-pairs comparison). Only a bucket
  // of exactly two candidates that also run with opposite winding is a valid
  // pairing — the manifold-crease signature `boundaryLoopOfFaceSet` already
  // relies on (see the module-level comment). Anything else (one unmatched
  // candidate, more than two, or matching-but-same-winding) is left
  // unresolved: an open mesh boundary, or an ambiguous/coincidental
  // arrangement this layer deliberately does not guess at.
  const buckets = new Map();
  for (const candidate of singleFaceCandidates) {
    const posA = exactPositionKey(positionById.get(candidate.a));
    const posB = exactPositionKey(positionById.get(candidate.b));
    const key = [posA, posB].sort().join('|');
    const list = buckets.get(key);
    if (list) list.push(candidate);
    else buckets.set(key, [candidate]);
  }

  const geometricEdgeOccurrences = [];
  for (const candidates of buckets.values()) {
    if (candidates.length === 2) {
      const [e1, e2] = candidates;
      const oppositeWinding =
        exactPositionKey(positionById.get(e1.a)) === exactPositionKey(positionById.get(e2.b)) &&
        exactPositionKey(positionById.get(e1.b)) === exactPositionKey(positionById.get(e2.a));
      if (oppositeWinding) {
        unionFind.union(e1.a, e2.b);
        unionFind.union(e1.b, e2.a);
        geometricEdgeOccurrences.push([e1, e2]);
        continue;
      }
    }
    for (const candidate of candidates) geometricEdgeOccurrences.push([candidate]);
  }
  for (const welded of weldedComplete) geometricEdgeOccurrences.push(welded.occurrences);

  // Corners: one per union-find group. A group's members are exactly the
  // vertex ids transitively unioned by successfully-paired edges — never a
  // blind global position pass — so an isolated vertex that merely happens
  // to share a coordinate with something unrelated stays its own singleton.
  const groupsByRoot = new Map();
  for (const vertex of topology.vertices) {
    const root = unionFind.find(vertex.id);
    const list = groupsByRoot.get(root);
    if (list) list.push(vertex.id);
    else groupsByRoot.set(root, [vertex.id]);
  }
  const cornerIdByVertexId = new Map();
  const cornersById = new Map();
  for (const members of groupsByRoot.values()) {
    const sortedMembers = [...members].sort();
    const id = contentId(sortedMembers);
    for (const memberId of sortedMembers) cornerIdByVertexId.set(memberId, id);
    cornersById.set(id, {
      id,
      vertexIds: sortedMembers,
      // A fresh copy, never the canonical vertex's own array: every member
      // of this group is exact-position-equal by construction (that is what
      // put them in one group), but this is a DERIVED, read-only result —
      // handing out the live `topology.vertices[i].position` reference would
      // let a caller mutate canonical topology through it.
      position: [...positionById.get(sortedMembers[0])],
      faceIds: new Set(),
      regionIds: new Set(),
      edgeIds: new Set(),
    });
  }
  for (const face of topology.faces) {
    const regionId = regionIds[regionIndexByFaceId.get(face.id)];
    for (const vertexId of face.vertexIds) {
      const corner = cornersById.get(cornerIdByVertexId.get(vertexId));
      corner.faceIds.add(face.id);
      corner.regionIds.add(regionId);
    }
  }

  // Coalesce by final derived edge id, not one entry per occurrence group:
  // mixed welded/reconstructed occurrence groups can resolve to the same
  // (cornerA, cornerB) pair after union-find grouping (e.g. a welded raw
  // edge and a separately position-paired boundary edge whose endpoints both
  // land in the same two corners), and each `edges[]` entry must be unique
  // by id — a later consumer keyed by `edge.id` must never see two records
  // silently overwrite each other. `cornerIds` is never ambiguous across
  // occurrence groups sharing one id (the id IS the sorted corner pair), so
  // only the region/face/topology-edge incidence sets need merging.
  const edgesById = new Map();
  for (const occurrences of geometricEdgeOccurrences) {
    const cornerA = cornerIdByVertexId.get(occurrences[0].a);
    const cornerB = cornerIdByVertexId.get(occurrences[0].b);
    const id = edgeId(cornerA, cornerB);
    let entry = edgesById.get(id);
    if (!entry) {
      entry = { id, cornerIds: [cornerA, cornerB].sort(), regionIds: new Set(), faceIds: new Set(), topologyEdgeIds: new Set() };
      edgesById.set(id, entry);
    }
    for (const o of occurrences) {
      entry.regionIds.add(regionIds[o.regionIndex]);
      entry.faceIds.add(o.faceId);
      entry.topologyEdgeIds.add(o.rawKey);
    }
  }
  for (const entry of edgesById.values()) {
    cornersById.get(entry.cornerIds[0]).edgeIds.add(entry.id);
    cornersById.get(entry.cornerIds[1]).edgeIds.add(entry.id);
  }
  const edges = [...edgesById.values()]
    .map((entry) => ({
      id: entry.id,
      cornerIds: entry.cornerIds,
      regionIds: [...entry.regionIds].sort(),
      faceIds: [...entry.faceIds].sort(),
      topologyEdgeIds: [...entry.topologyEdgeIds].sort(),
    }))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const edgeIdsByRegionId = new Map(regionIds.map((id) => [id, new Set()]));
  for (const edge of edges) {
    for (const regionId of edge.regionIds) edgeIdsByRegionId.get(regionId).add(edge.id);
  }

  const regions = regionsRaw
    .map((region, index) => {
      const id = regionIds[index];
      const cornerIdSet = new Set(region.boundaryVertexIds.map((vertexId) => cornerIdByVertexId.get(vertexId)));
      return {
        id,
        faceIds: region.faceIds,
        normal: region.normal,
        cornerIds: [...cornerIdSet].sort(),
        edgeIds: [...(edgeIdsByRegionId.get(id) ?? [])].sort(),
      };
    })
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const corners = [...cornersById.values()]
    .map((corner) => ({
      id: corner.id,
      vertexIds: corner.vertexIds,
      position: corner.position,
      faceIds: [...corner.faceIds].sort(),
      regionIds: [...corner.regionIds].sort(),
      edgeIds: [...corner.edgeIds].sort(),
    }))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  return {
    regions,
    corners,
    edges,
    diagonalTopologyEdgeIds: diagonalTopologyEdgeIds.sort(),
  };
}
